// v2.15.5 — Modale "Demander une adresse à un contact" (côté demandeur).

import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function AddressRequestShareModal({ open, userId, onClose }) {
  const [requesterName, setRequesterName] = useState("");
  const [contactHint, setContactHint] = useState("");
  const [created, setCreated] = useState(null); // {share_url, prefilled_messages, expires_at}
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const reset = () => { setRequesterName(""); setContactHint(""); setCreated(null); setLoading(false); };
  const close = () => { reset(); onClose?.(); };

  const createRequest = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/v3/address-requests/create`, {
        user_id: userId,
        requester_name: requesterName.trim() || null,
        contact_name_hint: contactHint.trim() || null,
      });
      if (!r.data?.ok) throw new Error(r.data?.error || "create_failed");
      setCreated(r.data);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally { setLoading(false); }
  };

  const shareNative = async () => {
    const msg = created?.prefilled_messages?.generic || created.share_url;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Demande d'adresse AYA", text: msg, url: created.share_url });
      } catch (_e) { /* user cancelled */ }
    } else {
      toast.message("Partage natif non supporté — utilise un des 4 boutons en bas");
    }
  };

  const shareWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(created.prefilled_messages.whatsapp)}`, "_blank");
  const shareSms = () => { window.location.href = `sms:?body=${encodeURIComponent(created.prefilled_messages.sms)}`; };
  const shareEmail = () => { window.location.href = `mailto:?subject=${encodeURIComponent("Demande d'adresse AYA")}&body=${encodeURIComponent(created.prefilled_messages.email)}`; };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(created.share_url); toast.success("✅ Lien copié"); }
    catch { toast.error("Copie échouée"); }
  };

  return (
    <div data-testid="request-share-modal" style={overlay}>
      <div style={modal}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 18 }}>💬 Demander une adresse</h3>
          <button onClick={close} data-testid="request-share-close" style={closeBtn}>✕</button>
        </div>

        {!created ? (
          <>
            <p style={{ fontSize: 12, color: "#cbd5e1", marginTop: 0 }}>
              Envoie un lien à un contact pour récupérer son adresse métrique sécurisée. Aucune installation nécessaire de son côté.
            </p>
            <label style={lbl}>Ton nom (visible par le contact)</label>
            <input data-testid="request-requester-name" type="text" value={requesterName} onChange={(e) => setRequesterName(e.target.value)} placeholder="Ex: Koffi K." style={inp} />
            <label style={lbl}>Nom du contact (optionnel)</label>
            <input data-testid="request-contact-hint" type="text" value={contactHint} onChange={(e) => setContactHint(e.target.value)} placeholder="Ex: Maman, mon client..." style={inp} />
            <button data-testid="request-create-btn" onClick={createRequest} disabled={loading} style={{ ...ctaPrimary, opacity: loading ? 0.6 : 1 }}>
              {loading ? "..." : "📤 Générer le lien"}
            </button>
          </>
        ) : (
          <>
            <div style={{ background: "rgba(34,197,94,0.12)", padding: "10px 12px", borderRadius: 8, marginBottom: 12, fontSize: 12, color: "#86EFAC" }}>
              ✅ Lien créé — expire dans 48h
            </div>
            <div data-testid="request-share-link" style={{
              background: "rgba(255,255,255,0.05)", padding: "8px 10px", borderRadius: 6, marginBottom: 12,
              fontFamily: "monospace", fontSize: 11, color: "#93C5FD", wordBreak: "break-all",
            }}>{created.share_url}</div>

            <button data-testid="request-share-native-btn" onClick={shareNative} style={ctaPrimary}>
              📤 Partager via mon téléphone
            </button>

            <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
              <button data-testid="request-share-whatsapp" onClick={shareWhatsApp} style={{ ...ctaSecondary, background: "#25D366", color: "#fff" }}>📱 WhatsApp</button>
              <button data-testid="request-share-sms" onClick={shareSms} style={ctaSecondary}>💬 SMS</button>
              <button data-testid="request-share-email" onClick={shareEmail} style={ctaSecondary}>📧 Email</button>
              <button data-testid="request-share-copy" onClick={copyLink} style={ctaSecondary}>📋 Copier</button>
            </div>
            <p style={{ fontSize: 11, color: "#94a3b8", marginTop: 12, textAlign: "center" }}>
              💡 Tu seras notifié dès que ton contact aura répondu.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };
const modal = { background: "#0F172A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, width: "100%", maxWidth: 480, padding: 18, color: "#F1F5F9" };
const closeBtn = { background: "transparent", border: "none", color: "#94a3b8", fontSize: 20, cursor: "pointer" };
const lbl = { display: "block", fontSize: 11, color: "#94a3b8", marginTop: 8, marginBottom: 4 };
const inp = { width: "100%", padding: 8, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)", color: "#fff", borderRadius: 6, fontSize: 13, boxSizing: "border-box" };
const ctaPrimary = { width: "100%", padding: "10px 14px", background: "linear-gradient(135deg,#3B82F6,#60A5FA)", border: "none", color: "#fff", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 700, marginTop: 8 };
const ctaSecondary = { padding: "8px 10px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)", color: "#CBD5E1", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 };
